import { CheckCircleFilled, ClearOutlined, CloseCircleOutlined, InfoCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { Button, Card, Col, Divider, Flex, Row, Space, Typography, theme } from 'antd';
import type { FC } from 'react';
import { TestTubeColor } from '../../components/TestTubeColor.tsx';
import type { Label, ScannedOrder } from '../../hooks/store/useScannedLabelsStore.ts';
import { PrintButton } from '../../PrintButton.tsx';
import { testTubeColorProperties, translateSpecimenFeature } from '../../utils/enumHelpers.ts';
import { patientFullName } from '../../utils/patient.ts';

export const Content: FC<{
  scannedOrders: ScannedOrder[];
  scannedLabelIds: string[];
  setScannedLabelIds: (labelIds: string[]) => void;
  setScannedOrders: (orders: ScannedOrder[]) => void;
  setDetailsRequestId: (detailsRequestId: string) => void;
  printLabel: (label: Label) => Promise<void>;
}> = ({ scannedOrders, scannedLabelIds, setDetailsRequestId, setScannedLabelIds, setScannedOrders, printLabel }) => {
  const { token } = theme.useToken();

  return scannedOrders.map(order => (
    <div key={order.id}>
      <Flex justify="space-between">
        <Typography.Title
          level={4}
          className={css`
            display: flex;
            align-items: center;
          `}
          // inline, so it wins over the heading colour without !important
          style={{ color: order.availableLabels === order.orderLabels.length ? token.green7 : token.red7 }}
        >
          <span>
            Auftrag <span className={monoFont}>{order.number}</span> - {order.request.doctor.name}
          </span>
        </Typography.Title>
        <Flex gap="small">
          <Button icon={<InfoCircleOutlined />} onClick={() => setDetailsRequestId(order.request.id)} />
          <Button
            icon={<ClearOutlined />}
            onClick={() => {
              setScannedLabelIds(scannedLabelIds.filter(it => !order.orderLabels.map(it => it.id).includes(it)));
              setScannedOrders(scannedOrders.filter(it => it.id !== order.id));
            }}
          />
        </Flex>
      </Flex>

      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Space size="middle">
          <span
            className={css`
              font-weight: bold;
            `}
          >
            {patientFullName(order.request.patientData)}
          </span>
          <Typography.Text type="secondary">Vorhandene Proben: {order.availableLabels}</Typography.Text>
          <Typography.Text type="secondary">Benötigte Proben: {order.orderLabels.length}</Typography.Text>
        </Space>
        <Row gutter={[16, 16]}>
          {order.orderLabels.map(label => {
            const done = scannedLabelIds.includes(label.id);
            return (
              <Col xs={24} md={12} lg={8} xxl={6} key={label.id}>
                <Card
                  size="small"
                  type="inner"
                  title={
                    <Flex gap="small">
                      {done ? (
                        <CheckCircleFilled style={{ color: token.colorSuccess, fontSize: '20px' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: token.colorError, fontSize: '20px' }} />
                      )}
                      <span
                        className={css`
                          text-overflow: ellipsis;
                          overflow: hidden;
                          white-space: nowrap;
                        `}
                      >
                        {label.name}
                      </span>
                    </Flex>
                  }
                  extra={
                    <Flex align="center">
                      <PrintButton
                        className={css`
                          padding: 0;
                          margin: 0;
                          height: 20px;
                        `}
                        // inline, so it wins over the icon-only button width without !important
                        style={{ width: 20 }}
                        icon={<PrinterOutlined />}
                        type="link"
                        onClick={() => printLabel(label)}
                      />
                    </Flex>
                  }
                  style={{
                    height: '100%',
                  }}
                  styles={{
                    header: {
                      backgroundColor: done ? token.colorSuccessBg : token.colorErrorBg,
                    },
                    body: {
                      color: token.colorTextSecondary,
                    },
                  }}
                >
                  <Flex justify="space-between" gap="small">
                    <span>
                      <span className={monoFont}>{label.number}</span>
                      {label.text && ` / ${label.text}`}
                      {label.text2 && ` / ${label.text2}`}
                    </span>
                    <TestTubeColor withText={false} properties={testTubeColorProperties(label.color)} />
                  </Flex>
                  <div>{label.location}</div>
                  <div>{label.features.map(it => translateSpecimenFeature(it)).join(', ')}</div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Space>
      <Divider />
    </div>
  ));
};
